import { useState, type FormEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { createSubmission, submissionsKey, type StoreApp } from '@/api/endpointsStores';
import type { Build } from '@/api/schemas';
import { FormField, fieldA11y } from '@/components/FormField';
import { ProblemAlert } from '@/components/ProblemAlert';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Segmented } from '@/features/access/bits';
import { RELEASE_STATUS_OPTIONS, type ReleaseStatus } from '@/features/profiles/bindingSubmitModel';
import { submitBuildBody, submitBuildErrors, type SubmitBuildForm } from './submissionsModel';

interface Props {
  build: Build;
  storeApps: StoreApp[];
  initial: SubmitBuildForm;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Submit one build to Google Play (P29 phase 2): one decision with a few fields, so a dialog. The API applies the rules. */
export function SubmitBuildDialog({ build, storeApps, initial, open, onOpenChange }: Props) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="submit-build-dialog" className="sm:max-w-lg" aria-describedby="submit-build-desc">
        {open ? <SubmitForm build={build} storeApps={storeApps} initial={initial} onDone={() => onOpenChange(false)} /> : null}
      </DialogContent>
    </Dialog>
  );
}

function useSubmit(build: Build, onDone: () => void) {
  const qc = useQueryClient();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [key] = useState(() => crypto.randomUUID());
  const submit = async (v: SubmitBuildForm) => {
    setPending(true);
    setError(null);
    try {
      const s = await createSubmission(submitBuildBody(build.id, v), key);
      toast.success(`Build #${build.number} submitted to ${s.track}`, { description: 'Follow it in the Google Play card.' });
      onDone();
    } catch (err) {
      setError(err);
    } finally {
      setPending(false);
      void qc.invalidateQueries({ queryKey: submissionsKey(build.id) });
    }
  };
  return { submit, pending, error };
}

function SubmitForm({ build, storeApps, initial, onDone }: { build: Build; storeApps: StoreApp[]; initial: SubmitBuildForm; onDone: () => void }) {
  const [v, setV] = useState(initial);
  const [touched, setTouched] = useState(false);
  const { submit, pending, error } = useSubmit(build, onDone);
  const errors = touched ? submitBuildErrors(v) : {};
  const set = (patch: Partial<SubmitBuildForm>) => setV((x) => ({ ...x, ...patch }));
  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (Object.keys(submitBuildErrors(v)).length) return;
    void submit(v);
  };
  return (
    <form onSubmit={onSubmit} noValidate className="flex min-h-0 flex-col">
      <DialogHeader>
        <DialogTitle>Submit build #{build.number} to Google Play</DialogTitle>
        <DialogDescription id="submit-build-desc">The build&apos;s AAB goes to the track you choose. The app&apos;s rules decide which tracks {build.environment.name} builds may reach.</DialogDescription>
      </DialogHeader>
      <DialogBody className="flex flex-col gap-4">
        <ProblemAlert error={error} testId="submit-build-problem" />
        <FormField id="submit-build-store-app" label="Google Play app" required error={errors.storeAppId}>
          <Select value={v.storeAppId} onValueChange={(storeAppId) => storeAppId && set({ storeAppId })}>
            <SelectTrigger data-testid="submit-build-store-app" className="w-full min-w-0" aria-label="Google Play app"><SelectValue placeholder="Choose the app" /></SelectTrigger>
            <SelectContent>{storeApps.map((s) => <SelectItem key={s.id} value={s.id}>{s.identifier}</SelectItem>)}</SelectContent>
          </Select>
        </FormField>
        <ReleaseFields v={v} set={set} error={errors.track} />
        {v.track.trim() === 'production' ? <ConfirmProduction checked={v.confirmProduction} onChange={(confirmProduction) => set({ confirmProduction })} error={errors.confirmProduction} /> : null}
      </DialogBody>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onDone} className="max-sm:h-11">Cancel</Button>
        <Button type="submit" data-testid="submit-build-send" loading={pending} className="max-sm:h-11">Submit</Button>
      </DialogFooter>
    </form>
  );
}

const TRACK_HINT = 'internal, alpha (closed testing), beta (open testing), production or a custom track';

function ReleaseFields({ v, set, error }: { v: SubmitBuildForm; set: (patch: Partial<SubmitBuildForm>) => void; error: string | undefined }) {
  return (
    <FormField id="submit-build-track" label="Track" required error={error} hint={TRACK_HINT}>
      <div className="flex flex-col gap-3">
        <Input data-testid="submit-build-track" value={v.track} onChange={(e) => set({ track: e.target.value })} className="font-mono" spellCheck={false} maxLength={64} {...fieldA11y('submit-build-track', error, TRACK_HINT)} />
        <Segmented<ReleaseStatus> label="Release" testId="submit-build-status" value={v.releaseStatus} onChange={(releaseStatus) => set({ releaseStatus })} options={RELEASE_STATUS_OPTIONS.map((o) => ({ value: o.value, label: o.label, title: o.description }))} />
        {v.releaseStatus === 'inProgress' ? (
          <label className="flex items-center gap-2 text-[13px]">
            <span>First release to</span>
            <Input data-testid="submit-build-rollout" inputMode="numeric" value={v.rolloutPercent} onChange={(e) => set({ rolloutPercent: e.target.value.replace(/[^0-9]/g, '') })} className="h-8 w-16 text-right font-mono max-sm:h-10" aria-label="Staged rollout percent" />
            <span>% of the track&apos;s users</span>
          </label>
        ) : null}
      </div>
    </FormField>
  );
}

function ConfirmProduction({ checked, onChange, error }: { checked: boolean; onChange: (v: boolean) => void; error: string | undefined }) {
  return (
    <div className="flex flex-col gap-1.5 rounded-lg border border-dashed px-3 py-2.5">
      <div className="flex items-start gap-3">
        <Checkbox id="submit-build-confirm" data-testid="submit-build-confirm" checked={checked} onCheckedChange={(c) => onChange(c === true)} className="mt-0.5" aria-invalid={error ? true : undefined} />
        <Label htmlFor="submit-build-confirm" className="cursor-pointer text-[13px] leading-snug">I confirm this release goes to production, where customers download the app</Label>
      </div>
      {error ? <p className="text-xs text-destructive" data-testid="submit-build-confirm-error">{error}</p> : null}
    </div>
  );
}
